/** Baseline slide width charts were tuned for (full presentation). */
export const CHART_WIDTH_BASELINE = 1920;

const DEFAULT_HC_SPACING = {
  top: 10,
  right: 10,
  bottom: 15,
  left: 10,
};

/** Scale pixel values used in Highcharts options (margins, spacing, tick intervals). */
export function scaleChartPx(value: number, containerWidth: number): number {
  const width =
    containerWidth > 0 ? containerWidth : CHART_WIDTH_BASELINE;
  const scaled = value * (width / CHART_WIDTH_BASELINE);
  return scaled < 1 ? 1 : scaled;
}

/**
 * Highcharts default plot margins are fixed pixels (~10–15px). In narrow
 * preview panes (e.g. Delad sida) they dominate unless scaled with the chart area.
 */
export function highchartsSpacing(containerWidth: number): {
  spacingTop: number;
  spacingRight: number;
  spacingBottom: number;
  spacingLeft: number;
} {
  return {
    spacingTop: scaleChartPx(DEFAULT_HC_SPACING.top, containerWidth),
    spacingRight: scaleChartPx(DEFAULT_HC_SPACING.right, containerWidth),
    spacingBottom: scaleChartPx(DEFAULT_HC_SPACING.bottom, containerWidth),
    spacingLeft: scaleChartPx(DEFAULT_HC_SPACING.left, containerWidth),
  };
}

/** Polar kompasser ska inte använda highchartsSpacing – det rubbar centrering på Helsida. */
export function clearHighchartsSpacing(chart: Record<string, unknown>): void {
  delete chart.spacingTop;
  delete chart.spacingRight;
  delete chart.spacingBottom;
  delete chart.spacingLeft;
}

export function applyPolarCompassSizing(
  chartOptions: any,
  containerWidth: number,
  defaults: { labelDistance?: number; plotLineWidth?: number } = {},
): void {
  const w = containerWidth > 0 ? containerWidth : CHART_WIDTH_BASELINE;
  const ratio = w / CHART_WIDTH_BASELINE;
  const labelDistance =
    defaults.labelDistance != null ? defaults.labelDistance : 25;
  const plotLineWidth =
    defaults.plotLineWidth != null ? defaults.plotLineWidth : 4;
  if (chartOptions.chart) {
    clearHighchartsSpacing(chartOptions.chart);
    chartOptions.chart.margin = [0, 0, 0, 0];
    chartOptions.chart.spacingTop = 0;
    chartOptions.chart.spacingRight = 0;
    chartOptions.chart.spacingBottom = 0;
    chartOptions.chart.spacingLeft = 0;
  }
  if (!chartOptions.pane) {
    chartOptions.pane = {};
  }
  chartOptions.pane.center = ['50%', '50%'];
  chartOptions.pane.size = ratio < 0.35 ? '72%' : ratio < 0.55 ? '78%' : '82%';
  if (chartOptions.xAxis && chartOptions.xAxis.labels) {
    chartOptions.xAxis.labels.distance = scaleChartPx(labelDistance, w);
  }
  const plotLines = chartOptions.xAxis && chartOptions.xAxis.plotLines;
  if (plotLines && Array.isArray(plotLines)) {
    for (const line of plotLines) {
      if (line && line.width != null) {
        line.width = scaleChartPx(plotLineWidth, w);
      }
    }
  }
}

export type CompassSlideLayout = 'full' | 'split' | 'auto';

export function isNarrowCompassContext(containerWidth: number): boolean {
  const w = containerWidth > 0 ? containerWidth : CHART_WIDTH_BASELINE;
  return w / CHART_WIDTH_BASELINE < 0.6;
}

function usesCompactCompassLegend(
  layout: CompassSlideLayout,
  containerWidth: number,
): boolean {
  if (layout === 'full') {
    return false;
  }
  if (layout === 'split') {
    return true;
  }
  return isNarrowCompassContext(containerWidth);
}

/**
 * Bransch-/Konkurrentkompass: samma flytande legend som Bolagskompass.
 * Pane-storlek skalas något på Delad sida (compact).
 */
export function applyMultiSeriesCompassLayout(
  chartOptions: any,
  containerWidth: number,
  layout: CompassSlideLayout = 'auto',
): void {
  const w = containerWidth > 0 ? containerWidth : CHART_WIDTH_BASELINE;
  const compact = usesCompactCompassLegend(layout, w);

  applyPolarCompassSizing(chartOptions, w, {
    labelDistance: 25,
    plotLineWidth: 4,
  });

  if (!chartOptions.pane) {
    chartOptions.pane = {};
  }
  chartOptions.pane.size = compact ? '78%' : '84%';
  chartOptions.pane.center = ['50%', '50%'];

  const legend = chartOptions.legend;
  if (legend) {
    // Samma som Bolagskompass: flytande vänster så polar-plotten centreras i hela ytan
    // (legend under diagram förskjuter plot-arean och ser vänsterjusterad ut).
    legend.floating = true;
    legend.align = 'left';
    legend.verticalAlign = 'top';
    legend.layout = 'vertical';
    legend.x = 0;
    legend.y = 0;
    legend.borderWidth = scaleChartPx(1, w);
    legend.padding = scaleChartPx(compact ? 8 : 16, w);
  }

  const seriesOpts =
    chartOptions.plotOptions && chartOptions.plotOptions.series;
  if (seriesOpts) {
    seriesOpts.lineWidth = scaleChartPx(1, w);
    const hover = seriesOpts.marker && seriesOpts.marker.states;
    if (hover && hover.hover) {
      hover.hover.radius = scaleChartPx(8, w);
    }
  }
}

/** Polar diagram: storlek efter slot och legend-läge. */
export function fitPolarCompassChartSize(
  chartOptions: any,
  slotEl: HTMLElement | null | undefined,
  containerWidth?: number,
  layout: CompassSlideLayout = 'auto',
): { width: number; height: number } | null {
  if (!slotEl || !chartOptions || !chartOptions.chart) {
    return null;
  }
  const w = slotEl.clientWidth;
  const h = slotEl.clientHeight;
  if (w < 1 || h < 1) {
    return null;
  }

  let width: number;
  let height: number;
  if (layout === 'full') {
    width = w;
    height = h;
  } else {
    const size = Math.min(w, h);
    width = size;
    height = size;
  }

  chartOptions.chart.width = width;
  chartOptions.chart.height = height;
  return { width, height };
}

export interface HighchartsReflowTarget {
  setSize: (w: number, h: number, animate?: boolean) => void;
  reflow: () => void;
}

/** Uppdatera Highcharts-storlek efter slot (krävs utöver reflow för reaktiva options). */
export function applyPolarCompassChartSize(
  highchartsChart: HighchartsReflowTarget | null | undefined,
  chartOptions: any,
  slotEl: HTMLElement | null | undefined,
  layout: CompassSlideLayout = 'auto',
): void {
  const size = fitPolarCompassChartSize(chartOptions, slotEl, undefined, layout);
  if (!highchartsChart || !size) {
    return;
  }
  highchartsChart.setSize(size.width, size.height, false);
  highchartsChart.reflow();
}

/** Vattenfallsdiagram i slides: skalade marginaler, fyll behållaren (ingen %-höjd). */
export function applySlideWaterfallChartSizing(
  chartOptions: any,
  containerWidth: number,
  extraRightSpacingPx = 20,
): void {
  const w = containerWidth > 0 ? containerWidth : CHART_WIDTH_BASELINE;
  if (!chartOptions.chart) {
    chartOptions.chart = {};
  }
  const spacing = highchartsSpacing(w);
  chartOptions.chart.spacingTop = spacing.spacingTop;
  chartOptions.chart.spacingRight = Math.max(
    spacing.spacingRight,
    scaleChartPx(extraRightSpacingPx, w),
  );
  chartOptions.chart.spacingBottom = spacing.spacingBottom;
  chartOptions.chart.spacingLeft = spacing.spacingLeft;
  delete chartOptions.chart.height;
  delete chartOptions.chart.width;
}

/** Y-axel tick-avstånd för vattenfall – undvik för täta ticks i smala förhandsgranskningar. */
export function waterfallTickPixelInterval(
  containerWidth: number,
  baselinePx = 150,
  minPx = 48,
): number {
  const w = containerWidth > 0 ? containerWidth : CHART_WIDTH_BASELINE;
  return Math.max(scaleChartPx(baselinePx, w), minPx);
}
